import React, { ReactNode, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { UserFormStates, useUserJourneyContext } from "@context/UserJourneyContext";
import useServiceHook from "@hooks/useServiceHook";

import { NavBar } from "@components/navigation/NavBar/NavBar";
import Button, { ButtonStyle } from "@components/buttons/Button/Button";

import { PlanService } from "@services/SubscriptionPlans";
import { getPricingRecordsByPotential, PricingRecordLineItem, PricingRecordView } from "@services/quotes/PricingRecords";

import { getZohoContactLink } from "@utils/misc/functions";
import { Country } from "@utils/constants/localisation";
import LoadingSpinner from "@utils/loadingSpinner";
import { AUPotentialPlanPreference, CBPAllowedPlans, GBPotentialPlanPreference } from "@utils/planOptions";

import PriceBreakdownComponent from "@views/QuoteBuilder/PriceBreakdownComponent";
import PlanSummaryComponent from "@views/QuoteBuilder/PlanSummaryComponent";
import QuoteStatusBanner from "@views/QuoteBuilder/QuoteStatusBanner";


function calculateAddonByReducedTerm(labelCode: string, quoteAddons: PricingRecordLineItem[], reducedTermLength: number): string {
    const addon = quoteAddons.find(item => item.line_item_label_code === labelCode && item.included === true);

    if (!addon || !addon.price_excluding_gst) {
        return "N/A";
    }

    const addonValue = Number(addon.price_excluding_gst);
    const totalValue = addonValue * reducedTermLength;

    return totalValue.toFixed(2);
}

function mapPricingRecord(pricingRecordData: PricingRecordView) {
    // Used to map the pricing record to cbpOnboard in UserJourneyContext
    const termLengthWeeks = Number(pricingRecordData.term_length.replace("y", "")) * 52;
    const termReductionWeeks = Number(pricingRecordData.term_reduction);
    const reducedTermLengthWeeks = termLengthWeeks - termReductionWeeks;

    return {
        PricingRecordId: String(pricingRecordData.pricing_record_id),
        ZohoContactId: String(pricingRecordData.zoho_contact.id),
        Country: pricingRecordData.country,
        State: pricingRecordData.state.toUpperCase(),  // Hopefully this is temp workaround for lowercase value from BE
        Plan: pricingRecordData.plan,
        VehicleCondition: pricingRecordData.vehicle_condition,
        TermLength: Number(pricingRecordData.term_length.replace("y", "")),  // Expected structure is "<d>y", where d is a the digit representing the nb of years
        Make: pricingRecordData.make,
        Model: pricingRecordData.model,
        ModelYear: pricingRecordData.model_year,
        TermReduction: pricingRecordData.term_reduction != null ? String(pricingRecordData.term_reduction) : "0",
        ReducedTermLengthWeeks: reducedTermLengthWeeks,
        ReplacementTyres: String(pricingRecordData.replacement_tyres ?? ""),
        ReplacementBrakePadSet: String(pricingRecordData.replacement_brake_pad_set ?? ""),
        ReplacementOrMachiningOfBrakeDiscs: String(pricingRecordData.replacement_or_machining_of_brake_discs ?? ""),
        WeeklyDistanceAllowance: String(pricingRecordData.weekly_distance_allowance),
        ExcessDistanceCost: String(pricingRecordData.excess_distance_cost),
        completedDatetime: pricingRecordData.completed_datetime,
        vehicleColour: pricingRecordData.vehicle_colour,
        vehicleRegistration: pricingRecordData.vehicle_registration,
        vehicleDeliveryEta: pricingRecordData.vehicle_delivery_eta,
        vehicleDriveawayPrice: String(pricingRecordData.vehicle_driveaway_price),
        setupFeeIncludingGst: String(pricingRecordData.setup_fee_including_gst),
        setupFeeExcludingGst: String(pricingRecordData.setup_fee_excluding_gst),
        quoteAddons: pricingRecordData.pricing_record_line_items,
        MinimumTerm: pricingRecordData.minimum_period_weeks,
        TripCount: pricingRecordData.trip_count,
        PricingRecordSections: pricingRecordData.pricing_record_sections,
    };
};


const SectionTopBottomSpacing = (
    { marginTop, marginBottom, children }: { marginTop: string, marginBottom: string, children: ReactNode }
) => (
    <div style={{ marginTop, marginBottom }}>
        {children}
    </div>
);
 
function QuoteViewer() {
    const navigate = useNavigate();

    const { formState: { selectedPotential }, updateFormState } = useUserJourneyContext();
    const { custId, country } = useParams() as { custId: string; country: Country; };

    const zoho_contact_url = getZohoContactLink(country, custId);
    const ZohoPotentialId = selectedPotential.id;

    const [planId, setPlanId] = useState<string | null>(null);

    const [loading, , , pricingRecord] = useServiceHook(
        getPricingRecordsByPotential,
        [country, ZohoPotentialId],
        Boolean(ZohoPotentialId),
        []
    );

    useEffect(() => {
        if (pricingRecord && pricingRecord.pricing_record_id != null) {
            const extractedPlanId = pricingRecord.pricing_record_line_items.find(
                (item: PricingRecordLineItem) => item.line_item_label_code === "A1" && item.included === true
            ).chargebee_item_key
            setPlanId(extractedPlanId);

            let plan: CBPAllowedPlans;
            if (pricingRecord.zoho_potential.Plan == AUPotentialPlanPreference.FlexiOwn){
                plan = CBPAllowedPlans.SplendFlexiOwn;
            } else if (pricingRecord.zoho_potential.Plan == AUPotentialPlanPreference.PreOwnedFlexiOwn) {
                plan = CBPAllowedPlans.SplendRentToOwnUsedVehicle;
            } else if (pricingRecord.zoho_potential.Plan == AUPotentialPlanPreference.Flexi) {
                plan = CBPAllowedPlans.SplendFlexi;
            } else if (pricingRecord.zoho_potential.Plan == GBPotentialPlanPreference.FlexiOwn) {
                plan = CBPAllowedPlans.GBFlexiOwn;
            } else if (pricingRecord.zoho_potential.Plan == GBPotentialPlanPreference.PreLovedFlexiOwn) {
                plan = CBPAllowedPlans.PreLovedFlexiOwn;
            }

            else {
                throw Error("Pricing record potential has a plan that's different from supported FOv2 plans.");
            }

            const c2Addon = pricingRecord.pricing_record_line_items.find(
                (item: PricingRecordLineItem) => item.line_item_label_code === "C2" && item.included === true
            );
            const c2LabelValue = c2Addon ? c2Addon.line_item_label_value?.replace('$', '') : "1500";

            const mappedRecord = mapPricingRecord(pricingRecord);
            const a1TotalValue = calculateAddonByReducedTerm("A1", pricingRecord.pricing_record_line_items, mappedRecord.ReducedTermLengthWeeks);
            const a2TotalValue = calculateAddonByReducedTerm("A2", pricingRecord.pricing_record_line_items, Math.min(156, mappedRecord.ReducedTermLengthWeeks));
            const a3TotalValue = calculateAddonByReducedTerm("A3", pricingRecord.pricing_record_line_items, mappedRecord.ReducedTermLengthWeeks);

            const totalAddonsValue = [a1TotalValue, a2TotalValue, a3TotalValue]
                .filter(value => value !== "N/A")
                .reduce((sum, value) => sum + Number(value), 0)
                .toFixed(2);

            updateFormState(
                UserFormStates.cbpOnboard,
                {
                    ...mappedRecord,
                    // this overrides the Plan value that comes from the pricing record (which is 'fo')
                    // to allow the FE and BE to operate on already known values.
                    Plan: plan,
                    PlanId: extractedPlanId,
                    PricingTemplateId: pricingRecord.pricing_template_id,
                    LossAndDamageWaiverValue: c2LabelValue,
                    A1TotalValue: a1TotalValue,
                    A2TotalValue: a2TotalValue,
                    A3TotalValue: a3TotalValue,
                    TotalAddonsValue: totalAddonsValue,
                }
            );
            // The plan field from driverDetails is heavily used in the big Onboarding component.
            // It's set here (duplicating the plan field from cbpOnboarding context) to allow
            // many existing mechanisms to continue using it as before and to allow less code changes.
            updateFormState(
                UserFormStates.driverDetails,
                { plan: plan }
            );
        }
    }, [pricingRecord]);

    const [planLoading, , , planDetails] = useServiceHook(
        PlanService.getPlan,
        [planId, country],
        planId != null,
        [planId],
        `There was an error getting the Chargebee plan '${planId}'. PLease check if it exists in Chargebee.`
    );

    useEffect(() => {
        if (planDetails) {
            updateFormState(
                UserFormStates.contractDetails,
                {
                    NSWWeeklyIncentiveExclGST: planDetails.NSWWeeklyIncentiveExclGST,
                    NSWTotalIncentiveExclGST: planDetails.NSWTotalIncentiveExclGST,
                    planName: planDetails.name,
                }
            )
        }
    }, [planDetails]);

    return (<>
        <NavBar pageTitle="Sales Quote" />
        {loading !== false || planLoading !== false
            ? <LoadingSpinner />
            : <>
                <div style={{ padding: "1rem" }}>
                    <QuoteStatusBanner
                        status={pricingRecord.status}
                        zohoSignQuoteDetail={pricingRecord.zoho_sign_quote_detail}
                        customerName={pricingRecord.zoho_potential?.Contact_Name?.name || ""}
                    />

                    <SectionTopBottomSpacing marginTop="2vh" marginBottom="2vh">
                        <PlanSummaryComponent
                            pricingRecordState={pricingRecord}
                        />
                    </SectionTopBottomSpacing>

                    <SectionTopBottomSpacing marginTop="2vh" marginBottom="2vh">
                        <PriceBreakdownComponent
                            pricingRecord={pricingRecord}
                            showTotalWeeklySubscriptionPrice={true}
                            showSetupFee={true}
                            defaultExpanded={true}
                        />
                    </SectionTopBottomSpacing>
                </div>

                <SectionTopBottomSpacing marginTop="0vh" marginBottom="1vh" >
                    <Button
                        label='Onboarding'
                        onClickFunc={() => navigate(`/onboarding/${country}/${custId}/quoted-vehicle-assignment`)}
                        btnType={ButtonStyle.primary}
                    />
                </SectionTopBottomSpacing>

                <SectionTopBottomSpacing marginTop="1vh" marginBottom="2.5vh" >
                    <Button
                        label='View Potential'
                        onClickFunc={() => window.open(zoho_contact_url, '_blank')}
                        btnType={ButtonStyle.secondary}
                    />
                </SectionTopBottomSpacing>
            </>}
    </>);
};


export default QuoteViewer;
